import { notificationApi } from "#/api/notificationApi";
import { showSuccessNotification } from "#/components/notifications";
import { useAuth } from "#/hook/useAuth";
import { useClient } from "#/hook/useClient";
import { NOTIFICATION_QUERY_KEYS, useNotificationActions } from "#/hooks/useNotificationActions";
import { UserRole } from "#/types/user";
import { ActionIcon, Badge, Button, Indicator, Popover, Text } from "@mantine/core";
import { IconBell, IconChecks } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import NotificationDrawer from "./NotificationDrawer";

const NotificationBell = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { client } = useClient();
  const [opened, setOpened] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState<number | undefined>();

  const themeColor = client?.color || "#3f81ef";
  const { markAsRead, markAllAsRead } = useNotificationActions();

  const { data: unreadCount = 0 } = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.unreadCount,
    queryFn: async () => {
      const response = await notificationApi.getUnreadCount();
      return response.data.count;
    },
    refetchInterval: 30000, // Poll every 30 seconds
    enabled: user?.role !== UserRole.ADMIN,
  });
  const hasUnread = typeof unreadCount === "number" && unreadCount > 0;

  const { data: recentNotifications } = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.recent,
    queryFn: async () => {
      const response = await notificationApi.getAll({ page: 1, limit: 5 });
      return response.data.data;
    },
    // Only fetched while the popover is open, and refreshed on every reopen
    // so a notification read elsewhere never shows as unread here.
    enabled: opened && user?.role !== UserRole.ADMIN,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate(undefined, {
      onSuccess: () => {
        showSuccessNotification({ message: t("notifications.allMarkedRead") });
      },
    });
  };

  // Only show for client users
  if (user?.role === UserRole.ADMIN) {
    return null;
  }

  return (
    <>
      <Popover opened={opened} onChange={setOpened} position="bottom-end" width={420} shadow="lg">
        <Popover.Target>
          <div className="pt-1">
            <Indicator disabled={!hasUnread} label={unreadCount} size={18} color="red" className="text-xs" >
            <ActionIcon
              variant="filled"
              color="black"
              size="lg"
              radius="xl"
              onClick={() => setOpened((o) => !o)}
            >
              <IconBell size={22} stroke={1.5} />
            </ActionIcon>
          </Indicator>
          </div>
        </Popover.Target>

        <Popover.Dropdown className="p-0 border-0 shadow-2xl rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b flex items-center justify-between gap-3 bg-black">
            <div className="flex items-center gap-2">
              <Text fw={700} size="lg" className="text-white">{t("notifications.title")}</Text>
              {hasUnread && (
                <Badge size="sm" variant="filled" className="bg-white text-black">{unreadCount}</Badge>
              )}
            </div>
            {hasUnread && (
              <Button
                size="xs"
                variant="white"
                color="black"
                leftSection={<IconChecks size={14} />}
                loading={markAllAsRead.isPending}
                onClick={handleMarkAllAsRead}
              >
                {t("notifications.markAllRead")}
              </Button>
            )}
          </div>

          {!recentNotifications || recentNotifications.length === 0 ? (
            <div className="py-16 px-8 text-center bg-white">
              <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <IconBell size={32} className="text-gray-400" stroke={1.5} />
              </div>
              <Text className="text-gray-600" size="sm" fw={600} mb={4}>
                {t("notifications.noNotifications")}
              </Text>
              <Text className="text-gray-500" size="xs">
                {t("notifications.noNotificationsDescription")}
              </Text>
            </div>
          ) : (
            <div className="max-h-[480px] overflow-y-auto flex flex-col gap-4 bg-slate-100/50 p-4">
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className="px-5 py-4 flex flex-col cursor-pointer transition-all items-start gap-1 bg-white shadow shadow-black/20 rounded-lg"
                  onClick={() => {
                    if (!notification.seen) markAsRead.mutate(notification.id);
                    setSelectedNotificationId(notification.id);
                    setOpened(false);
                    setDrawerOpened(true);
                  }}
                >
                  <Text fw={600} size="sm" className="text-gray-900">
                    {notification.title}
                  </Text>
                  <Text size="xs" className="text-gray-600 line-clamp-2 leading-relaxed">
                    {notification.description}
                  </Text>
                  <div className="flex items-center gap-2 mt-4">
                    <Text size="xs">
                      {moment(notification.createdAt).fromNow()}
                    </Text>
                    {!notification.seen && (
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: themeColor }} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {recentNotifications && recentNotifications.length > 0 && (
            <div
              className="px-5 py-3.5 text-center bg-white cursor-pointer hover:bg-gray-100 transition-colors border-t border-t-gray-600"
              onClick={() => {
                setOpened(false);
                setDrawerOpened(true);
              }}
            >
              <Text size="sm" fw={600} style={{ color: themeColor }}>
                {t("notifications.viewAll")} →
              </Text>
            </div>
          )}
        </Popover.Dropdown>
      </Popover>

      <NotificationDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        preselectedNotificationId={selectedNotificationId}
      />
    </>
  );
};

export default NotificationBell;
